import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";
import { Badge } from "catalyst-ui-kit/badge";
import Currency from "components/format/currency/currency";
import { ProductDetails } from "types/ProductCatalogue";
import { formatQuantity } from "./product-format";

interface Props {
  product: ProductDetails;
  /** Percent of the product's VAT rate; undefined while the rates load. */
  vatPercent: number | undefined;
  onEdit: () => void;
}

export default function ProductHeader({ product, vatPercent, onEdit }: Props) {
  const { t } = useTranslation();
  const isService = product.type === "service";
  const quantity = product.quantity || 0;
  const reserved = product._reservedQuantity || 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6" data-testid="product-header">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="min-w-0 break-words text-xl font-semibold text-gray-900">{product.title}</h1>
            <Badge color={isService ? "indigo" : "amber"}>
              {t(isService ? "products_page_type_service" : "products_page_type_product")}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {t("sku")} <span className="font-mono text-gray-700">{product.sku}</span>
            {product.supplier_sku && (
              <>
                {" · "}
                {t("supplier_sku")} <span className="font-mono text-gray-700">{product.supplier_sku}</span>
              </>
            )}
          </p>
          {product.titleEn && <p className="mt-1 text-sm text-gray-500">{product.titleEn}</p>}
          {product.description && <p className="mt-2 text-sm text-gray-700">{product.description}</p>}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium hover:bg-gray-50 max-sm:min-h-11"
        >
          <Pencil aria-hidden className="h-4 w-4" />
          {t("edit")}
        </button>
      </div>

      <dl className="mt-5 grid grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-4 text-sm">
        <Figure label={t("products_page_price")}>
          <Currency value={product.price} isSymbol isDecimal />
        </Figure>
        <Figure label={t("vat")}>{vatPercent === undefined ? "–" : `${vatPercent} %`}</Figure>
        <Figure label={t("type")}>{t(isService ? "products_page_type_service" : "products_page_type_product")}</Figure>
        {!isService && (
          <>
            <Figure label={t("products_page_in_stock")}>{formatQuantity(quantity)}</Figure>
            <Figure label={t("products_page_reserved")} tone={reserved ? "text-amber-700" : undefined}>
              {formatQuantity(reserved)}
            </Figure>
            <Figure label={t("products_page_free")} tone="text-emerald-700">
              {formatQuantity(quantity - reserved)}
            </Figure>
          </>
        )}
      </dl>
    </div>
  );
}

function Figure({ label, tone, children }: { label: string; tone?: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className={`mt-1 break-words text-lg font-semibold tabular-nums ${tone || "text-gray-900"}`}>{children}</dd>
    </div>
  );
}
